import { NotyfService } from "./services/notyf/notyf.service";
import { EnvironmentService } from "./services/environment/environment.service";
import { ApiService } from "./services/api/api.service";
import { FcmService } from "./services/fcm/fcm.service";
import { AuthService } from "src/app/services/auth/auth.service";
import { CartService } from "./services/cart/cart.service";
import { SettingsService } from "./services/settings/settings.service";
import { Component, OnChanges } from "@angular/core";
import { AlertController, Platform } from "@ionic/angular";
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Browser } from '@capacitor/browser';
import { Category } from "./interfaces/category";
import { CategoriesService } from "./services/categories/categories.service";
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { App } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { Capacitor } from "@capacitor/core";

const CART_EXTERNAL_REFRESH_TIMEOUT = 60 * 60 * 1000; // 1 hour;

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
})
export class AppComponent {
  loading = true;
  loadingModal: HTMLIonLoadingElement;
  currentPlatform: string = "";
  cart_timeout;
  slug = 'all';

  categories: Category[] = [];
  platform_type: any = '';
  app_version: string = "2.0.0";

  constructor(
    private platform: Platform,
    private settings: SettingsService,
    private auth: AuthService,
    private fcm: FcmService,
    private categoriesService: CategoriesService,
    private cartService: CartService,
    private api: ApiService,
    private alertController: AlertController,
    private env: EnvironmentService,
    private notyf: NotyfService,
  ) {
    
    this.slug = this.env.settings.salon.slug;
    this.initializeApp();
  }

  async initializeApp() {

    // this.api.getApiUrl();

    this.platform.ready().then(async () => {
      this.platform_type = await this.loadPlatform();  
      // this.getCategories();
      this.registerCartRefresh();

      if (this.currentPlatform !== "web") {        
        await ScreenOrientation.lock({ orientation: 'portrait' });        
        const info = await App.getInfo();
        this.app_version = info.version;
        await this.configureStatusBar() 
        await this.fcm.setupFCM();   
        await SplashScreen.hide();        
        await this.checkVersion();
      }

    });
  }


  async configureStatusBar() {
    if (Capacitor.isNativePlatform()) {
      await StatusBar.setStyle({ style: Style.Default });
      await StatusBar.show();
    }
  
    if (Capacitor.getPlatform() === 'android') {
      
      await EdgeToEdge.enable();
      await StatusBar.setBackgroundColor({ color: '#fcd3cd' });
      await StatusBar.setStyle({ style: Style.Dark });
    }
  }

  registerCartRefresh() {
    this.auth.loggedIn$.subscribe((isLoggedIn) => {
      if (isLoggedIn === true) {
        this.cartService.getCartExternal().catch((err) => {
          console.log("error getting external cart");
          console.log({ err });
          this.auth.logout();
          this.notyf.error("You have been logged out.");
        });

        this.cart_timeout = setInterval(() => {
          this.cartService.getCartExternal().catch((err) => {
            console.log("error getting external cart");
            console.log({ err });
          });
        }, CART_EXTERNAL_REFRESH_TIMEOUT);
      } else {
        clearInterval(this.cart_timeout);
      }
    });
  }

  async checkVersion() {
    const response: any = await this.api.get("version-check", {
      version: this.app_version,
      salon_id: this.env.api.salon_id,
      platform: this.currentPlatform,
    }).toPromise();
  
    // Safely extract values with defaults
    const forceUpdate = response?.force_update ?? false;
    const isValid = response?.valid ?? true;
  
    if (forceUpdate == 'yes' || forceUpdate == true) {
      if (isValid === false) {
        const alert = await this.alertController.create({
          header: "Update Available",
          backdropDismiss: false,
          message: "A newer version of this app is available. Please update to continue.",
          buttons: [
            {
              text: "Update",
              handler: () => {
                this.openAppStore(response?.link);
                return false;
              },
            },
          ],
        });
        await alert.present();
      }
    }
  }
  

  async loadPlatform() {
    this.currentPlatform = await this.settings.getPlatform();
  }

  async openAppStore(appIdOrPackage: string) {
    let storeUrl = '';
    const isFullUrl = appIdOrPackage.startsWith('http://') || appIdOrPackage.startsWith('https://');
  
    if (isFullUrl) {
      // Use the full URL directly
      storeUrl = appIdOrPackage;
    } else {
      // Build platform-specific store URLs
      const platform = Capacitor.getPlatform();
  
      if (platform === 'android') {
        storeUrl = `https://play.google.com/store/apps/details?id=${appIdOrPackage}`;
      } else if (platform === 'ios') {
        storeUrl = `https://apps.apple.com/app/id${appIdOrPackage}`;
      } else {
        console.warn('Unsupported platform for app store link');
        return;
      }
    }
  
    try {
      await Browser.open({ url: storeUrl });
    } catch (error) {
      console.error('Error opening app store URL:', error);
      this.notyf.error('An error occurred while opening the app store link.');
    }
  }
}
